/**
 * Quality / Settings › External tools: this project's opt-in to the tools the Flow admin set up for the organization
 * (GET /quality/external `org`). "Send this project to SonarQube" (with an optional project key; empty: Flow names it
 * flow-<project id>) and "Use Semgrep AppSec Platform rules". A tool the organization has not set up stays off and
 * disabled, with a line saying to ask the Flow admin. The values live in the settings' `external` block and are saved
 * with the rest of the settings (the Save bar).
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Switch } from 'antd';
import { getExternal } from '../../services/QualityService';
import './QualityTools.css';

const ASK_ADMIN = 'Your organization has not set it up yet: ask your Flow admin (Flow admin › Settings › Quality tools).';

export default function QualityExternalSettings({ projectId, external = null, onChange, readOnly = false, errorAt = () => null }) {
  const [org, setOrg] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    Promise.resolve().then(() => getExternal(projectId)).then((answer) => { if (alive) { setOrg(answer?.org || {}); } }).catch(() => { if (alive) { setFailed(true); } });
    return () => { alive = false; };
  }, [projectId]);

  const sonar = external?.sonar || { on: false, projectKey: null };
  const semgrep = external?.semgrep || { on: false };
  const sonarReady = Boolean(org?.sonar?.configured);
  const semgrepReady = Boolean(org?.semgrep?.configured);
  const setSonar = (patch) => onChange({ ...(external || {}), sonar: { ...sonar, ...patch } });
  const setSemgrep = (patch) => onChange({ ...(external || {}), semgrep: { ...semgrep, ...patch } });
  const where = org?.sonar?.url ? ` (${org.sonar.url}${org.sonar.organization ? `, organization ${org.sonar.organization}` : ''})` : '';
  const error = (path) => errorAt(path);

  return (
    <section aria-labelledby="quality-external-title" className="quality-card" data-testid="quality-external-settings">
      <div className="quality-pad-head">
        <h2 id="quality-external-title">External tools</h2>
        <p className="quality-muted">Your organization&apos;s own tools, set up once by the Flow admin. Each project chooses whether to use them; their results show in the External tools check.</p>
      </div>
      {failed && <p className="quality-muted quality-small quality-pad-foot" role="status">Could not read what your organization has set up: the switches stay as saved.</p>}
      <div className="quality-rule" data-testid="quality-external-sonar">
        <Switch
          aria-label="Send this project to SonarQube"
          checked={Boolean(sonar.on)}
          disabled={readOnly || !org || (!sonarReady && !sonar.on)}
          onChange={(on) => setSonar({ on })}
          size="small"
        />
        <span className="quality-grow">
          <strong>Send this project to SonarQube</strong>: its code is analysed by your organization&apos;s SonarQube or SonarCloud{where}, and its quality gate shows here.
          {org && !sonarReady && <span className="quality-muted quality-small quality-external-hint">{ASK_ADMIN}</span>}
          {sonarReady && (
            <label className="quality-field quality-external-key">
              <span>Project key (optional)</span>
              <input
                aria-label="SonarQube project key"
                className="quality-input quality-mono"
                disabled={readOnly || !sonar.on}
                maxLength={400}
                onChange={(e) => setSonar({ projectKey: e.target.value.trim() === '' ? null : e.target.value })}
                placeholder={`flow-${projectId}`}
                value={sonar.projectKey || ''}
              />
            </label>
          )}
          {error('external.sonar') && <span className="quality-field-error" role="alert">{error('external.sonar')}</span>}
        </span>
      </div>
      <div className="quality-rule" data-testid="quality-external-semgrep">
        <Switch
          aria-label="Use Semgrep AppSec Platform rules"
          checked={Boolean(semgrep.on)}
          disabled={readOnly || !org || (!semgrepReady && !semgrep.on)}
          onChange={(on) => setSemgrep({ on })}
          size="small"
        />
        <span className="quality-grow">
          <strong>Use Semgrep AppSec Platform rules</strong>: Semgrep checks the code with your organization&apos;s own rules and policies; its findings show in the External tools check.
          {org && !semgrepReady && <span className="quality-muted quality-small quality-external-hint">{ASK_ADMIN}</span>}
          {error('external.semgrep') && <span className="quality-field-error" role="alert">{error('external.semgrep')}</span>}
        </span>
      </div>
    </section>
  );
}

QualityExternalSettings.propTypes = {
  // (path) => string | null: a 400's error at that settings path.
  errorAt: PropTypes.func,
  // The settings' `external` block: `{ sonar: { on, projectKey }, semgrep: { on } }`.
  external: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
};
